'use client';
import { useEffect, useState } from 'react';
import type { Locale } from '@/i18n/i18n';
import { Link } from '@/i18n/navigation';

type GtagParams = {
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  wait_for_update: number;
};

const denied: GtagParams = {
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  ad_storage: 'denied',
  analytics_storage: 'denied',
  wait_for_update: 500,
};

const granted: GtagParams = {
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  ad_storage: 'granted',
  analytics_storage: 'granted',
  wait_for_update: 500,
};

export const CookiesClient = ({
  locale,
  content,
  accept,
  decline,
  more,
}: {
  locale: Locale;
  content: string | TrustedHTML;

  accept: string | null | undefined;
  decline: string | null | undefined;
  more: string | null | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(() =>
    getLocalStorage('cookie_consent', false),
  );

  useEffect(() => {
    const savedConsent = getLocalStorage('cookie_consent', undefined);
    const newValue = savedConsent ? granted : denied;

    if (typeof savedConsent === 'undefined' || savedConsent === 'false')
      return setOpen(true);

    if (typeof window !== 'undefined')
      window?.gtag?.('consent', 'update', newValue);
  }, [cookieConsent]);

  if (!open || cookieConsent) return null;

  return (
    <section
      className="cookie-card"
      aria-label={
        locale === 'el' ? 'Προτιμήσεις cookies' : 'Cookie preferences'
      }
    >
      <h2>
        {locale === 'el'
          ? 'Λίγα λόγια για τα cookies.'
          : 'A little about cookies.'}
      </h2>
      <div
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />

      <div className="cookie-actions">
        <button
          onClick={() => {
            setOpen(false);
            setCookieConsent(true);
            setLocalStorage('cookie_consent', true);
          }}
          className="pill pill-ink"
        >
          {accept}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            setCookieConsent(false);
            setLocalStorage('cookie_consent', false);
          }}
          className="pill pill-outline"
        >
          {decline}
        </button>
      </div>
      <Link target="_blank" href={`/privacy-policy`} className="cookie-more">
        {more}
      </Link>
    </section>
  );
};

export function getLocalStorage(key: string, defaultValue: any) {
  if (typeof window === 'undefined') return defaultValue;
  const stickyValue = localStorage.getItem(key);

  return stickyValue !== null && stickyValue !== 'undefined'
    ? JSON.parse(stickyValue)
    : defaultValue;
}

export function setLocalStorage(key: string, value: any) {
  if (typeof window === 'undefined') return;

  localStorage.setItem(key, JSON.stringify(value));
}
